/**
 * Mantém somente caracteres que podem ser enviados como texto para ESC/POS.
 * Caracteres de controle e Unicode invisível não podem chegar à impressora,
 * pois alguns modelos os interpretam como comandos de posicionamento.
 */
export function sanitizarTextoParaEscPos(valor: unknown): string {
    const textoSemAcentos = String(valor ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ç/g, 'c')
        .replace(/Ç/g, 'C');

    return Array.from(textoSemAcentos)
        .map((caractere) => {
            if (/\s/.test(caractere)) return ' ';

            const codigo = caractere.charCodeAt(0);
            return codigo >= 0x20 && codigo <= 0x7E ? caractere : '';
        })
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Formata um bloco de texto puro antes da conversão para bytes. Preserva os LF
 * do cupom, mas impede que controles ou Unicode dos dados virem comandos da
 * impressora. Não usar em blocos que já contenham comandos ESC/POS ou QR Code.
 */
export function formatarBlocoTextoParaEscPos(texto: string, largura: number): string {
    if (!Number.isInteger(largura) || largura <= 0) {
        throw new Error('A largura do texto ESC/POS deve ser um inteiro positivo');
    }

    return texto.split('\n').map((linha) => {
        const palavras = sanitizarTextoParaEscPos(linha).split(' ');
        const linhas: string[] = [];
        let linhaAtual = '';

        palavras.forEach((palavra) => {
            let restante = palavra;

            while (restante.length > 0) {
                const proximaLinha = linhaAtual ? `${linhaAtual} ${restante}` : restante;

                if (proximaLinha.length <= largura) {
                    linhaAtual = proximaLinha;
                    restante = '';
                } else if (linhaAtual) {
                    linhas.push(linhaAtual);
                    linhaAtual = '';
                } else {
                    linhas.push(restante.slice(0, largura));
                    restante = restante.slice(largura);
                }
            }
        });

        if (linhaAtual) linhas.push(linhaAtual);
        return linhas.join('\n');
    }).join('\n');
}

/**
 * Codifica texto puro com avanço explícito de uma linha (ESC d 1).
 * Algumas impressoras portáteis sobrepõem linhas do pagamento com LF/CRLF;
 * este é o avanço validado no teste físico. Comandos de fonte, corte e QR Code
 * devem continuar fora deste bloco, pois a entrada é sanitizada como texto.
 */
export function codificarTextoComAvancoEscPos(texto: string): number[] {
    const dados: number[] = [];
    const linhas = texto.split('\n');

    linhas.forEach((linha, indice) => {
        dados.push(...Array.from(sanitizarTextoParaEscPos(linha), caractere => caractere.charCodeAt(0)));

        if (indice < linhas.length - 1) {
            dados.push(0x1B, 0x64, 0x01);
        }
    });

    return dados;
}
